import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import BoxOptions from '@CommonFunctionalDisplay/BoxOptions';
import { FormControlLabel, FormGroup} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Rating from '@mui/material/Rating';
import StarIcon from '@mui/icons-material/Star';
import { styled } from '@mui/material/styles';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LensIcon from '@mui/icons-material/Lens';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import * as _ from 'lodash';
import Menu from '@mui/material/Menu';
import Select from '@mui/material/Select';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { IoClose as Close } from 'react-icons/io5';
import { BsPlusCircle as Add } from 'react-icons/bs';
import ordList from '@Assets/textEditor/ol.svg';
import unordList from '@Assets/textEditor/ul.svg';
import underline from '@Assets/textEditor/underline.svg';
import bold from '@Assets/textEditor/bold.svg';
import italics from '@Assets/textEditor/italics.svg';
import { EditorState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import BorderWhiteButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderWhiteButtonV2';
import FieldService from '@Helpers/URLHelpers/FieldService';
import FieldPageService from '@Helpers/URLHelpers/FieldPageService';
import ColorPicker from '@CommonFunctionalDisplay/ColorPicker';
import KaiziqResolveConnectionService from '@Helpers/URLHelpers/KaiziqResolveConnectionService';
import ColorizeIcon from '@mui/icons-material/Colorize';
import { Emoji } from 'emoji-picker-react';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import EmojiPicker from 'emoji-picker-react';
import ErrorHandler from '@Root/ErrorHandler';
import '@Partials/_ComStyles.scss';
import './FieldForm.scss';

export default function FieldForm(props) {

    const [field, setField] = useState({
        title: 'New Field',
        type: 'text',
        rules: [
            {
                id: 0,
                type: 'required',
                value: false
            },
            {
                id: 1,
                type: 'default',
                value: ''
            },
            {
                id: 2,
                type: 'width',
                value: 250
            }
        ]
    });
    const colorList = ['#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474', '#6080b0', '#9a60b0', '#6cb060',
            '#b0ae60', '#65412b', '#cc9963', '#e1bd83', '#60748b', '#744700', '#c8d579', '#5f8fba', '#3e6073', '#8e6dc1', '#473785',
           '#9158a9',  '#7f3f7a', '#583e84', '#073763', '#20124d', '#3d124d', '#64113f', '#780448', '#003e23', '#108764', '#1a4c3d',
            '#c8d77f', '#11888b', '#1a95bd', '#0d6c7b', '#25b56e', '#006e3d'];
    const [required, setRequired] = useState(false);
    const [title, setTitle] = useState('');
    const [defaultText, setDefaultText] = useState('');
    const [defaultTextLength, setDefaultTextLength] = useState(0);
    const [titleTextLength, setTitleTextLength] = useState(0);
    const [textSize, setTextSize] = useState(250);
    const [ratingRange, setRatingRange] = useState(5);
    const [ratingType, setRatingType] = useState('select');
    const [scaleIcon, setScaleIcon] = useState('star');
    const [ratingInitialLabel, setRatingInitialLabel] = useState('');
    const [ratingFinalLabel, setRatingFinalLabel] = useState('');
    const [fieldType, setFieldType] = useState('text');
    const [multiChoiceOptions, setMultiChoiceOptions] = useState([]);
    const [ratingRangeOptions, setRatingRangeOptions] = useState([]);
    const [informationEditorState, setInformationEditorState] = useState(EditorState.createEmpty());
    const [informationEditorSize, setInformationEditorSize] = useState(0);
    const [multiChoiceOptionsDefault, setMultiChoiceOptionsDefault] = useState(0);
    const [colorPickerColor, setColorPickerColor] = useState();
    const [defaultNumber, setDefaultNumber] = useState(0);
    const [numberMax, setNumberMax] = useState(null);
    const [numberMin, setNumberMin] = useState(null);
    const [numberDigitWidth, setNumberDigitWidth] = useState(10);
    const [refresh, setRefresh] = useState(false);
    const [anchorColorMenu, setAnchorColorMenu] = useState(null);
    const [showColorMenu, setShowColorMenu] = useState(false);
    const [optionEditIndex, setOptionEditIndex] = useState(0);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const [cursor, setCursor] = useState('auto');
    const [anchorEmojiPicker, setAnchorEmojiPicker] = useState(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [emojiEditIndex, setEmojiEditIndex] = useState();
    const errorHandler = new ErrorHandler();
    const fieldPageService = new FieldPageService();
    const fieldService = new FieldService();
    const kaiziqResolveConnectionService = new KaiziqResolveConnectionService();
    const { currentProjectId } = useReduxProject();
    const MAX_LENGTH = 5000;
    const className = 'fieldForm';

    const HeartStyledRating = styled(Rating)({
      '& .MuiRating-iconFilled': {
        color: '#ff6d75',
      },
      '& .MuiRating-iconHover': {
        color: '#ff3d47',
      },
    });

    const CircleStyledRating = styled(Rating)({
      '& .MuiRating-iconFilled': {
        color: '#2784B9d6',
      },
      '& .MuiRating-iconHover': {
        color: '#2784B9',
      },
    });

    useEffect(() => {
        checkDisabled();
    }, [refresh])

    const handleTitleChange = (e) => {
        let sanitizedTitle = DOMPurify.sanitize(e.target.value);
        let fieldObj = field
        fieldObj.title = sanitizedTitle;
        setField(fieldObj);
        setTitleTextLength(sanitizedTitle.length);
        setTitle(sanitizedTitle);
        setRefresh(!refresh);
    }

    const handleFieldTypeChange = (e) =>{
        let sanitizedFieldType = DOMPurify.sanitize(e.target.value);
        let fieldObj = field
        fieldObj.type = sanitizedFieldType;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'default');
        let defaultValue = ''
        if(e.target.value === 'text') {
            defaultValue = '';
        } if(e.target.value === 'phoneNumber') {
            defaultValue = '';
        } else if(e.target.value === 'multiChoice') {
            defaultValue = 0;
        } else if(e.target.value === 'rating') {
            let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'width');
            if(ruleIndex === -1) {
                let rule = {
                    id: fieldObj?.rules?.length + 1,
                    type: 'width',
                    value: 5
                }
                fieldObj?.rules?.push(rule);
            } else {
                fieldObj.rules[ruleIndex]['value'] = 5;
            }
            ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'ratingType');
            if(ruleIndex === -1) {
                let rule = {
                    id: fieldObj?.rules?.length + 1,
                    type: 'ratingType',
                    value: 'select'
                }
                fieldObj?.rules?.push(rule);
            } else {
                fieldObj.rules[ruleIndex]['value'] = 'select';
            }
            ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'scaleIcon');
                if(ruleIndex === -1) {
                    let ruleObj = {
                        id: fieldObj.rules?.length + 1,
                        type: 'scaleIcon',
                        value: 'star'
                    }
                    fieldObj.rules.push(ruleObj);
                } else {
                    fieldObj.rules[ruleIndex]['scaleIcon'] = 'star';
                }
            populateRatingRange(5);
        } else if(e.target.value === 'multiSelect') {
            defaultValue = [0];
        } else if(e.target.value === 'number') {
            defaultValue = 0;
        } else if(e.target.value === 'file') {
            defaultValue = null;
        }
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'default',
                value: defaultValue
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['value'] = defaultValue;
        }
        if(e.target.value === 'number') {
            let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'digitWidth');
            if(ruleIndex === -1) {
                let rule = {
                    id: field?.rules?.length + 1,
                    type: 'digitWidth',
                    value: 10
                }
                fieldObj?.rules?.push(rule);
                let rule2 = {
                    id: fieldObj.rules?.length + 1,
                    type: 'matchDigitWidth',
                    value: true
                }
                fieldObj?.rules?.push(rule2);
            } else {
                fieldObj.rules[ruleIndex]['value'] = 10;
            }
        }
        if(e.target.value === 'duration') {
            let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'maxDuration');
            if(ruleIndex === -1) {
                let rule = {
                    id: field?.rules?.length + 1,
                    type: 'maxDuration',
                    set: false
                }
                fieldObj?.rules?.push(rule);
            } else {
                fieldObj.rules[ruleIndex]['set'] = false;
            }
            ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'minDuration');
            if(ruleIndex === -1) {
                let rule = {
                    id: field?.rules?.length + 1,
                    type: 'minDuration',
                    set: false
                }
                fieldObj?.rules?.push(rule);
            } else {
                fieldObj.rules[ruleIndex]['set'] = false;
            }
        }
        if(e.target.value === 'duration') {
            let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'maxDuration');
            if(ruleIndex === -1) {
                let rule = {
                    id: field?.rules?.length + 1,
                    type: 'maxDuration',
                    set: false
                }
                fieldObj?.rules?.push(rule);
            } else {
                fieldObj.rules[ruleIndex]['set'] = false;
            }
            ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'minDuration');
            if(ruleIndex === -1) {
                let rule = {
                    id: field?.rules?.length + 1,
                    type: 'minDuration',
                    set: false
                }
                fieldObj?.rules?.push(rule);
            } else {
                fieldObj.rules[ruleIndex]['set'] = false;
            }
        }
        if(e.target.value === 'file') {
            let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'acceptTypes');
            if(ruleIndex === -1) {
                let ruleObj = {
                    id: fieldObj.rules?.length + 1,
                    type: 'acceptTypes',
                    value: [
                        '.png',
                        '.jpg',
                        '.jpeg',
                        '.pdf',
                        '.doc',
                        '.docx',
                        '.mp4'
                    ]
                }
                fieldObj.rules.push(ruleObj);
            }
        }
        setField(fieldObj);
        setFieldType(sanitizedFieldType)
        setRefresh(!refresh);
    }

    const handleDefaultTextChange = (e) => {
        let sanitizedDefaultText = DOMPurify.sanitize(e.target.value);
        let fieldObj = field
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'default');
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'default',
                value: sanitizedDefaultText
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['value'] = sanitizedDefaultText;
        }
        setField(fieldObj);
        setDefaultTextLength(sanitizedDefaultText.length);
        setDefaultText(sanitizedDefaultText);
        setRefresh(!refresh);
    }

    const handleTextSizeChange = (e) => {
        let textSize = e.target.value;
        if(( 1 <= textSize ) && ( textSize <= 5000 )) {
            let fieldObj = field
            let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'width');
            if(ruleIndex === -1) {
                let ruleObj = {
                    id: fieldObj.rules?.length + 1,
                    type: 'width',
                    value: textSize
                }
                fieldObj.rules.push(ruleObj);
            } else {
                fieldObj.rules[ruleIndex]['value'] = textSize;
            }
            setField(fieldObj);
            setTextSize(e.target.value);
            setRefresh(!refresh);
        }
    }

    const toggleMaxDuration = () => {
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'maxDuration');
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'maxDuration',
                set: true,
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['set'] = !fieldObj.rules[ruleIndex]['set'];
            fieldObj.rules[ruleIndex]['days'] = 0;
            fieldObj.rules[ruleIndex]['hours'] = 0;
            fieldObj.rules[ruleIndex]['minutes'] = 0;
            fieldObj.rules[ruleIndex]['seconds'] = 0;
        }
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const handleRatingRangeChange = (e) => {
        let range = e.target.value;
        if(range < 2) range = 2;
        if(range > 10) range = 10;
        let fieldObj = field
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'width');
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'width',
                value: range
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['value'] = range;
        }
        setField(fieldObj);
        setRatingRange(range);
        populateRatingRange(range);
        setRefresh(!refresh);
    }

    const handleRatingTypeChange = (e) => {
        let ratingType = e.target.value;
        let fieldObj = field
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'ratingType');
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'ratingType',
                value: ratingType
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['value'] = ratingType;
        }
        setField(fieldObj);
        setRatingType(ratingType);
        populateRatingRange(ratingRange);
        setRefresh(!refresh);
    }

    const handleRatingTypeScaleIconChange = (e) => {
        let scaleIcon = e.target.value;
        let fieldObj = field
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'scaleIcon');
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'scaleIcon',
                value: scaleIcon
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['value'] = scaleIcon;
        }
        setField(fieldObj);
        setScaleIcon(scaleIcon);
        setRefresh(!refresh);
    }

    const handleRatingTypeRangeInitialLabelChange = (e) => {
        let ratingInitialLabel = e.target.value;
        let fieldObj = field
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'ratingInitialLabel');
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'ratingInitialLabel',
                value: ratingInitialLabel
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['value'] = ratingInitialLabel;
        }
        setField(fieldObj);
        setRatingInitialLabel(ratingInitialLabel);
        setRefresh(!refresh);
    }

    const handleRatingTypeRangeFinalLabelChange = (e) => {
        let ratingFinalLabel = e.target.value;
        let fieldObj = field
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'ratingFinalLabel');
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'ratingFinalLabel',
                value: ratingFinalLabel
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['value'] = ratingFinalLabel;
        }
        setField(fieldObj);
        setRatingFinalLabel(ratingFinalLabel);
        setRefresh(!refresh);
    }

    const setMaxDurationDays = (e) => {
        let value = e.target.value;
        if(value < 0) value = 0;
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'maxDuration');
        fieldObj.rules[ruleIndex]['days'] = value;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const setMaxDurationHours = (e) => {
        let value = e.target.value;
        if(value < 0) value = 0;
        if(value > 24) value = 24;
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'maxDuration');
        fieldObj.rules[ruleIndex]['hours'] = value;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const setMaxDurationMinutes = (e) => {
        let value = e.target.value;
        if(value < 0) value = 0;
        if(value > 60) value = 60;
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'maxDuration');
        fieldObj.rules[ruleIndex]['minutes'] = value;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const setMaxDurationSeconds = (e) => {
        let value = e.target.value;
        if(value < 0) value = 0;
        if(value > 60) value = 60;
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'maxDuration');
        fieldObj.rules[ruleIndex]['seconds'] = value;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const toggleMinDuration = () => {
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'minDuration');
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'minDuration',
                set: true,
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['set'] = !fieldObj.rules[ruleIndex]['set'];
            fieldObj.rules[ruleIndex]['days'] = 0;
            fieldObj.rules[ruleIndex]['hours'] = 0;
            fieldObj.rules[ruleIndex]['minutes'] = 0;
            fieldObj.rules[ruleIndex]['seconds'] = 0;
        }
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const setMinDurationDays = (e) => {
        let value = e.target.value;
        if(value < 0) value = 0;
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'minDuration');
        fieldObj.rules[ruleIndex]['days'] = value;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const setMinDurationHours = (e) => {
        let value = e.target.value;
        if(value < 0) value = 0;
        if(value > 24) value = 24;
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'minDuration');
        fieldObj.rules[ruleIndex]['hours'] = value;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const setMinDurationMinutes = (e) => {
        let value = e.target.value;
        if(value < 0) value = 0;
        if(value > 60) value = 60;
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'minDuration');
        fieldObj.rules[ruleIndex]['minutes'] = value;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const setMinDurationSeconds = (e) => {
        let value = e.target.value;
        if(value < 0) value = 0;
        if(value > 60) value = 60;
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'minDuration');
        fieldObj.rules[ruleIndex]['seconds'] = value;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const handleNumberDigitChange = (e) => {
        let digitValue = e.target.value;
        if(( 1 <= digitValue ) && ( digitValue <= 20 )) {
            let fieldObj = field;
            let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'digitWidth');
            if(ruleIndex === -1) {
                let rule = {
                    id: field?.rules?.length + 1,
                    type: 'digitWidth',
                    value: digitValue
                }
                fieldObj?.rules?.push(rule);
            } else {
                fieldObj.rules[ruleIndex]['value'] = digitValue;
            }
            setField(fieldObj);
            setNumberDigitWidth(e.target.value);
            setRefresh(!refresh);
        }
    }

    const toggleMatchDigitWidth = () => {
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'matchDigitWidth');
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'matchDigitWidth',
                value: true
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['value'] = !fieldObj.rules[ruleIndex]['value'];
        }
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const handleDefaultNumberChange = (e) => {
        let defaultNumberValue = e.target.value;
        let numberWidth = defaultNumberValue.toString().length;
        if( numberWidth <= numberDigitWidth ) {
            let fieldObj = field;
            let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'default');
            if(ruleIndex === -1) {
                let ruleObj = {
                    id: fieldObj.rules?.length + 1,
                    type: 'default',
                    value: defaultNumberValue
                }
                fieldObj.rules.push(ruleObj);
            } else {
                fieldObj.rules[ruleIndex]['value'] = defaultNumberValue;
            }
            setField(fieldObj);
            setDefaultNumber(defaultNumberValue);
            setRefresh(!refresh);
        }
    }

    const handleMinInputNumberChange = (e) => {
        let minInputNumberValue = e.target.value;
        let numberWidth = minInputNumberValue.toString().length;
        if( numberWidth <= numberDigitWidth ) {
            let fieldObj = field;
            let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'rangeMin');
            if(ruleIndex === -1) {
                let ruleObj = {
                    id: fieldObj.rules?.length + 1,
                    type: 'rangeMin',
                    value: minInputNumberValue
                }
                fieldObj.rules.push(ruleObj);
            } else {
                fieldObj.rules[ruleIndex]['value'] = minInputNumberValue;
            }
            setField(fieldObj);
            setNumberMin(minInputNumberValue);
            setRefresh(!refresh);
        }
    }

    const handleMaxInputNumberChange = (e) => {
        let maxInputNumberValue = e.target.value;
        let numberWidth = maxInputNumberValue.toString().length;
        if( numberWidth <= numberDigitWidth ) {
            let fieldObj = field;
            let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'rangeMax');
            if(ruleIndex === -1) {
                let ruleObj = {
                    id: fieldObj.rules?.length + 1,
                    type: 'rangeMax',
                    value: maxInputNumberValue
                }
                fieldObj.rules.push(ruleObj);
            } else {
                fieldObj.rules[ruleIndex]['value'] = maxInputNumberValue;
            }
            setField(fieldObj);
            setNumberMax(maxInputNumberValue);
            setRefresh(!refresh);
        }
    }

    const toggleRequired = () => {
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'required');
        if (fieldObj.type === 'condition') {
            if (ruleIndex === -1) {
                fieldObj.rules.push({
                    id: fieldObj.rules.length + 1,
                    type: 'required',
                    value: true
                });
            } else {
                fieldObj.rules[ruleIndex]['value'] = true;
            }
            setField(fieldObj);
            setRequired(true);
            setRefresh(!refresh);
            return;
        }
        if (ruleIndex === -1) {
            fieldObj.rules.push({
                id: fieldObj.rules.length + 1,
                type: 'required',
                value: true
            });
        } else {
            fieldObj.rules[ruleIndex]['value'] = !fieldObj.rules[ruleIndex]['value'];
        }
        setField(fieldObj);
        setRequired(!required);
        setRefresh(!refresh);
    }

    const toggleDefaultOption = (index) => {
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'default');
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'default',
                value: index
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['value'] = index;
        }
        setField(fieldObj);
        setMultiChoiceOptionsDefault(index);
        setRefresh(!refresh);
    }

    const toggleAllowPriorDate = () => {
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'allowPrior');
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'allowPrior',
                value: true
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['value'] = !fieldObj.rules[ruleIndex]['value'];
        }
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const toggleAllowTimeSelect = () => {
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'timeSelect');
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'timeSelect',
                value: true
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['value'] = !fieldObj.rules[ruleIndex]['value'];
        }
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const handleOptionTitleChange = (e, index) => {
        let sanitizedTitle = DOMPurify.sanitize(e.target.value);
        let optionsObj = multiChoiceOptions;
        optionsObj[index]['title'] = sanitizedTitle;
        optionsObj[index]['value'] = index + 1;
        setMultiChoiceOptions(optionsObj);
        let fieldObj = field;
        fieldObj.options = optionsObj;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const handleOptionColorChange = (color) => {
        let optionsObj = multiChoiceOptions;
        optionsObj[optionEditIndex]['color'] = color.hex;
        setMultiChoiceOptions(optionsObj);
        setColorPickerColor(color.hex);
        let fieldObj = field;
        fieldObj.options = optionsObj;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const toggleFileType = (type) => {
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'acceptTypes');
        let fileIndex = fieldObj.rules[ruleIndex].value.findIndex((fileType) => fileType === type);
        if(fileIndex === -1) {
            fieldObj.rules[ruleIndex].value.push(type);
        } else {
            fieldObj.rules[ruleIndex].value = fieldObj.rules[ruleIndex].value.filter((fileType) => {return fileType !== type});
        }
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const addOption = (e) => {
        let optionsObj = multiChoiceOptions;
        let maxId =  Math.max(...multiChoiceOptions.map(option => option.id))
        if(maxId === null || maxId === undefined || isNaN(maxId) || maxId < 0) maxId = -1;
        let optionObj = {
            id: maxId + 1,
            title: 'New Option',
            color: colorList[(maxId + 1) % colorList?.length],
            value: multiChoiceOptions.length + 1,
            icon: 'none',
            rules: []
        }
        optionsObj.push(optionObj)
        setMultiChoiceOptions(optionsObj);
        let fieldObj = field;
        fieldObj.options = optionsObj;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const shiftOptionUp = (e, index) => {
        let optionsObj = multiChoiceOptions;
        let option = optionsObj[index];
        let upOption = optionsObj[index - 1];
        option.id = option.id - 1;
        option.value = option.value - 1;
        upOption.id = upOption.id + 1;
        upOption.value = upOption.value + 1;
        optionsObj[index] = upOption;
        optionsObj[index - 1] = option;
        setMultiChoiceOptions(optionsObj);
        let fieldObj = field;
        fieldObj.options = optionsObj;
        setField(fieldObj);
        console.log(fieldObj);
        setRefresh(!refresh);
    }

    const shiftOptionDown = (e, index) => {
        let optionsObj = multiChoiceOptions;
        let option = optionsObj[index];
        let downOption = optionsObj[index + 1];
        option.id = option.id + 1;
        option.value = option.value + 1;
        downOption.id = downOption.id - 1;
        downOption.value = downOption.value - 1;
        optionsObj[index] = downOption;
        optionsObj[index + 1] = option;
        setMultiChoiceOptions(optionsObj);
        let fieldObj = field;
        fieldObj.options = optionsObj;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const deleteOption = (index) => {
        let optionsObj = multiChoiceOptions;
        optionsObj.splice(index, 1);
        for(var i; i < optionsObj.length; i++) {
            optionsObj[i].id = i;
        }
        setMultiChoiceOptions(optionsObj);
        let fieldObj = field;
        let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'default');
        if(ruleIndex === -1) {
            let ruleObj = {
                id: fieldObj.rules?.length + 1,
                type: 'default',
                value: 0
            }
            fieldObj.rules.push(ruleObj);
        } else {
            fieldObj.rules[ruleIndex]['value'] = 0;
        }
        setMultiChoiceOptionsDefault(0);
        fieldObj.options = optionsObj;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const handleColorPickerMenu = (event, index) => {
        if(!showColorMenu){
            if(!!event) {
                setAnchorColorMenu(event.currentTarget);
                setShowColorMenu(true);
                setOptionEditIndex(index);
                setColorPickerColor(multiChoiceOptions[index]['color']);
            }
        } else {
            setAnchorColorMenu(null);
            setShowColorMenu(false);
        }
    }

    const clearRange = () => {
        setNumberMax(null);
        setNumberMin(null);
        setRefresh(!refresh);
    }

    const checkDisabled = () => {
        switch(fieldType) {
            case 'text':
                if(defaultTextLength > textSize) {
                    setIsSubmitDisabled(true)
                } else {
                    setIsSubmitDisabled(false)
                }
                break;
            case 'multiChoice':
                if(multiChoiceOptions.length < 2) {
                    setIsSubmitDisabled(true)
                } else {
                    setIsSubmitDisabled(false)
                }
                break;
            case 'multiSelect':
                if(multiChoiceOptions.length < 1) {
                    setIsSubmitDisabled(true)
                } else {
                    setIsSubmitDisabled(false)
                }
                break;
            case 'condition':
                if(multiChoiceOptions.length < 2) {
                    setIsSubmitDisabled(true)
                } else {
                    setIsSubmitDisabled(false)
                }
                break;
            case 'number':
                if((required && (defaultNumber.toString().length === 0)) ||
                    ((!!numberMin && !isNaN(numberMin) &&  (parseInt(numberMin) >= parseInt(numberMax) || parseInt(numberMin) >= parseInt(defaultNumber))) ||
                    (!!numberMax && !isNaN(numberMax) && (parseInt(numberMax) <= parseInt(numberMin) || parseInt(numberMax) <= parseInt(defaultNumber))))) {
                    setIsSubmitDisabled(true)
                } else {
                    setIsSubmitDisabled(false)
                }
                break;
            case 'file':
                let ruleIndex = field.rules.findIndex((rule) => rule.type === 'acceptTypes');
                if(field.rules[ruleIndex].value.length === 0) {
                    setIsSubmitDisabled(true);
                } else {
                    setIsSubmitDisabled(false);
                }
                break;
            case 'information':
                if(informationEditorSize === 0 || informationEditorSize >= 5000) {
                    setIsSubmitDisabled(true);
                } else {
                    setIsSubmitDisabled(false);
                }
                break;
            default :
                setIsSubmitDisabled(false)
        }
        if(titleTextLength === 0) setIsSubmitDisabled(true);
    }

    const _getLengthOfSelectedText = () => {
        const currentSelection = informationEditorState.getSelection();
        const isCollapsed = currentSelection.isCollapsed();
        let length = 0;
        if (!isCollapsed) {
            const currentContent = informationEditorState.getCurrentContent();
            const startKey = currentSelection.getStartKey();
            const endKey = currentSelection.getEndKey();
            const startBlock = currentContent.getBlockForKey(startKey);
            const isStartAndEndBlockAreTheSame = startKey === endKey;
            const startBlockTextLength = startBlock.getLength();
            const startSelectedTextLength = startBlockTextLength - currentSelection.getStartOffset();
            const endSelectedTextLength = currentSelection.getEndOffset();
            const keyAfterEnd = currentContent.getKeyAfter(endKey);
            if (isStartAndEndBlockAreTheSame) {
                length += currentSelection.getEndOffset() - currentSelection.getStartOffset();
            } else {
                let currentKey = startKey;
                while (currentKey && currentKey !== keyAfterEnd) {
                    if (currentKey === startKey) {
                        length += startSelectedTextLength + 1;
                    } else if (currentKey === endKey) {
                        length += endSelectedTextLength;
                    } else {
                        length += currentContent.getBlockForKey(currentKey).getLength() + 1;
                    }
                    currentKey = currentContent.getKeyAfter(currentKey);
                };
            }
        }
        return length;
    }

    const _handleBeforeInput = () => {
        const currentContent = informationEditorState.getCurrentContent();
        const currentContentLength = currentContent.getPlainText('').length;
        const selectedTextLength = _getLengthOfSelectedText();

        if (currentContentLength - selectedTextLength > MAX_LENGTH - 1) {
            return 'handled';
        }
    }

    const _handlePastedText = (pastedText) => {
        const currentContent = informationEditorState.getCurrentContent();
        const currentContentLength = currentContent.getPlainText('').length;
        const selectedTextLength = _getLengthOfSelectedText();
        if (currentContentLength + pastedText.length - selectedTextLength > MAX_LENGTH) {
            return true;
        }
    }

    const onEditorStateChange = (editorStateObj) => {
        let tempDetails = convertToRaw(editorStateObj.getCurrentContent());
        let size = 0;
        for(var i = 0; i < tempDetails.blocks.length; i++) {
            size += tempDetails.blocks[i].text.length;
        }
        setInformationEditorSize(size);
        setRefresh(!refresh);
        if(size < MAX_LENGTH){
            setInformationEditorState(editorStateObj);
            let fieldObj = field;
            let ruleIndex = fieldObj.rules.findIndex((rule) => rule.type === 'text');
            if(ruleIndex === -1) {
                let ruleObj = {
                    id: fieldObj.rules?.length + 1,
                    type: 'text',
                    value: JSON.stringify(convertToRaw(editorStateObj.getCurrentContent()))
                }
                fieldObj.rules.push(ruleObj);
            } else {
                fieldObj.rules[ruleIndex]['value'] = JSON.stringify(convertToRaw(editorStateObj.getCurrentContent()));
            }
            setField(fieldObj);
            fieldObj.value = JSON.stringify(convertToRaw(editorStateObj.getCurrentContent()));
        } else {
            setInformationEditorState(informationEditorState);
        }
    }

    const handleSubmit = async() => {
        setCursor('wait');
        try {
            let fieldObj = _.cloneDeep(field);
            fieldObj.tableType = 'kaiziqResolveConnection';
            fieldObj.tableId = props.kaiziqResolveConnection.id;
            if(fieldObj.type === 'condition') {
                let rules = fieldObj.rules[0];
                rules.value = true;
                fieldObj.rules[0] = rules;
            }
            fieldObj.rules = JSON.stringify(fieldObj.rules);
            fieldObj.projectId = currentProjectId;
            if(!!fieldObj.options) {
                fieldObj.options = JSON.stringify(fieldObj.options);
            } else {
                fieldObj.options = '[]';
            }
            fieldObj.filterAttribute1 = 'form';
            fieldObj = await fieldService.createField(fieldObj);
            fieldObj = JSON.parse(fieldObj.body);
            if(!!fieldObj) {
                //Update FieldPage
                let fieldsListObj = JSON.parse(props.fieldPage.fields);
                fieldsListObj.push(fieldObj.id);
                let fieldPageObj = props.fieldPage;
                fieldPageObj.fields = JSON.stringify(fieldsListObj);
                await fieldPageService.updateFieldPage(fieldPageObj);

                // Update KaiziqResolveConnection
                fieldsListObj = JSON.parse(props.kaiziqResolveConnection.details).fields['form'];
                fieldsListObj.push(fieldObj.id);
                let fieldHistoryObj = {
                    current: fieldObj.id,
                    fieldHistory: [fieldObj.id]
                };
                let detailsFieldHistoryObj = JSON.parse(props.kaiziqResolveConnection.details).fields['fieldHistory'];
                detailsFieldHistoryObj[fieldObj.id] = fieldHistoryObj;
                let detailsObj = JSON.parse(props.kaiziqResolveConnection.details);
                detailsObj.fields['form'] = fieldsListObj;
                detailsObj.fields['fieldHistory'] = detailsFieldHistoryObj;
                await kaiziqResolveConnectionService.updateKaiziqResolveConnectionDetails(props.kaiziqResolveConnection?.id, JSON.stringify(detailsObj));
                if (props?.onFieldCreated) {
                    props.onFieldCreated(fieldObj);
                } else if (props?.reloadData) {
                    await props.reloadData();
                }
                props.closePopup();
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const populateRatingRange = (range) => {
        let optionsList = [];
        for(var i = 0; i < range; i++) {
            let optionObj = {
                id: i,
                title: i + 1,
                value: i + 1,
                iconType: 'text',
                iconValue: null,
                iconColor: '#2784B9',
                rules: []
            }
            optionsList.push(optionObj)
        }
        let fieldObj = field;
        fieldObj.options = optionsList;
        setField(fieldObj);
        setRatingRangeOptions(optionsList);
    }

    const handleRatingRangeTitleChange = (e, index) => {
        let sanitizedTitle = DOMPurify.sanitize(e.target.value);
        let optionsObj = ratingRangeOptions;
        optionsObj[index]['title'] = sanitizedTitle;
        setRatingRangeOptions(optionsObj);
        let fieldObj = field;
        fieldObj.options = optionsObj;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const handleRatingRangeIconTypeChange = (iconType, index) => {
        let optionsObj = ratingRangeOptions;
        optionsObj[index]['iconType'] = iconType;
        let icon = {
            'activeSkinTone': 'neutral',
            'emoji': '😃',
            'imageUrl': 'https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/1f603.png',
            'isCustom': false,
            'names': [
                'smiley',
                'smiling face with open mouth'
            ],
            'unified': '1f603',
            'unifiedWithoutSkinTone': '1f603'
        }
        optionsObj[index]['iconValue'] = icon;
        setRatingRangeOptions(optionsObj);
        let fieldObj = field;
        fieldObj.options = optionsObj;
        setField(fieldObj);
        setRefresh(!refresh);
    }

    const handleEmojiPickerPopup = (event) => {
        if(!showEmojiPicker){
            if(!!event) {
                setAnchorEmojiPicker(event.currentTarget);
                setShowEmojiPicker(true);
            }
        } else {
            setAnchorEmojiPicker(null);
            setShowEmojiPicker(false);
        }
    }

    const handleEmojiClick = (e, index) => {
        setEmojiEditIndex(index);
        handleEmojiPickerPopup(e);
    }

    const handleEmojiPickerSelect = (icon) => {
        let optionsObj = ratingRangeOptions;
        optionsObj[emojiEditIndex]['iconValue'] = icon;
        setRatingRangeOptions(optionsObj);
        let fieldObj = field;
        fieldObj.options = optionsObj;
        setField(fieldObj);
        handleEmojiPickerPopup();
        setRefresh(!refresh);
    }

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}
            className={className}
            style={{cursor: cursor}}
            aria-labelledby='modal-field-view'>
                <div className={className + '__inner flex-column-center component-shadow'}>
                    <div className={className + '__inner__titleBar flex-row-center-space-between width-wfa'}>
                        <div className={className + '__inner__titleBar__title flex-row-center-1rem-gap semi-large-text semi-bold-text'}>
                            Add Field
                        </div>
                        <Close className={className + '__inner__titleBar__close close-button'}
                            disabled={cursor === 'wait'}
                            onClick={props.closePopup}/>
                    </div>
                    <div className={className + '__inner__body flex-column-flex-start gap-1rem width-wfa'}>
                        <div className={className + '__inner__body__title flex-column-flex-end width-wfa'}>
                            <TextField
                                placeholder='Enter title ...'
                                className={className + '__title__flex__textField width-wfa'}
                                type='text'
                                onChange={handleTitleChange}
                                inputProps={{ maxLength: 500 }}
                                disabled={cursor === 'wait'}
                                defaultValue={title}
                                maxRows='1'
                                variant='outlined'
                                required/>
                            <div className={className + '__title__flex__size flex-column-flex-end-2vh-gap semi-bold-text width-wfa '
                                + (titleTextLength > 450 ? ' red-text' : '')}>
                                {titleTextLength}/500
                            </div>
                        </div>
                        <div className={className + '__inner__body__attributes flex-column-flex-start gap-1rem width-wfa'}>
                            <div className={className + '__inner__body__attributes__fieldType flex-row-center-1rem-gap width-wfa'}>
                                <div className={className + '__inner__body__attributes__fieldType__t1 semi-bold-text'}>
                                    What is the Field Type?
                                </div>
                                <Select className={className + '__inner__body__attributes__fieldType__select standard-status-select '}
                                    variant='outlined'
                                    defaultValue={fieldType}
                                    onChange={handleFieldTypeChange}
                                    disabled={cursor === 'wait'}>
                                    <MenuItem value='text' className={className + '__inner__body__attributes__fieldType__select__option'}>
                                        <div className={className + '__inner__body__attributes__fieldType__select__option flex-row-center-1rem-gap'}>
                                            Text
                                        </div>
                                    </MenuItem>
                                    <MenuItem value='information' className={className + '__inner__body__attributes__fieldType__select__option'}>
                                        <div className={className + '__inner__body__attributes__fieldType__select__option flex-row-center-1rem-gap'}>
                                            Information
                                        </div>
                                    </MenuItem>
                                    <MenuItem value='multiChoice' className={className + '__inner__body__attributes__fieldType__select__option'}>
                                        <div className={className + '__inner__body__attributes__fieldType__select__option flex-row-center-1rem-gap'}>
                                            Multiple Choice (Single Select)
                                        </div>
                                    </MenuItem>
                                    <MenuItem value='multiSelect' className={className + '__inner__body__attributes__fieldType__select__option'}>
                                        <div className={className + '__inner__body__attributes__fieldType__select__option flex-row-center-1rem-gap'}>
                                            Multiple Choice (Multiple Select)
                                        </div>
                                    </MenuItem>
                                    <MenuItem value='condition' className={className + '__inner__body__attributes__fieldType__select__option'}>
                                        <div className={className + '__inner__body__attributes__fieldType__select__option flex-row-center-1rem-gap'}>
                                            Conditional Navigation
                                        </div>
                                    </MenuItem>
                                    <MenuItem value='duration' className={className + '__inner__body__attributes__fieldType__select__option'}>
                                        <div className={className + '__inner__body__attributes__fieldType__select__option flex-row-center-1rem-gap'}>
                                            Duration
                                        </div>
                                    </MenuItem>
                                    <MenuItem value='phoneNumber' className={className + '__inner__body__attributes__fieldType__select__option'}>
                                        <div className={className + '__inner__body__attributes__fieldType__select__option flex-row-center-1rem-gap'}>
                                            Phone Number
                                        </div>
                                    </MenuItem>
                                    <MenuItem value='rating' className={className + '__inner__body__attributes__fieldType__select__option'}>
                                        <div className={className + '__inner__body__attributes__fieldType__select__option flex-row-center-1rem-gap'}>
                                            Rating
                                        </div>
                                    </MenuItem>
                                    <MenuItem value='number' className={className + '__inner__body__attributes__fieldType__select__option'}>
                                        <div className={className + '__inner__body__attributes__fieldType__select__option flex-row-center-1rem-gap'}>
                                            Number
                                        </div>
                                    </MenuItem>
                                    <MenuItem value='date' className={className + '__inner__body__attributes__fieldType__select__option'}>
                                        <div className={className + '__inner__body__attributes__fieldType__select__option flex-row-center-1rem-gap'}>
                                            Date
                                        </div>
                                    </MenuItem>
                                    <MenuItem value='file' className={className + '__inner__body__attributes__fieldType__select__option'}>
                                        <div className={className + '__inner__body__attributes__fieldType__select__option flex-row-center-1rem-gap'}>
                                            File
                                        </div>
                                    </MenuItem>
                                </Select>
                            </div>
                            {
                                fieldType !== 'information' || fieldType !== 'condition' &&
                                    <div className={className + '__inner__body__attributes__required flex-row-center-1rem-gap width-wfa'}>
                                        <div className={className + '__inner__body__attributes__required__t1 semi-bold-text'}>
                                            Is this a Required Field?
                                        </div>
                                        <FormGroup className={className + '__inner__body__attributes__required__check'}>
                                            <FormControlLabel control={<Checkbox
                                                checked={field?.rules?.find((rule) => rule.type === 'required').value}
                                                disabled={cursor === 'wait'}
                                                onChange={toggleRequired}/>}/>
                                        </FormGroup>
                                    </div>
                            }
                            {
                                fieldType === 'multiChoice' &&
                                <div className={className + '__inner__body__attributes__multiChoice width-wfa flex-column-flex-start-1vh-gap'}>
                                    <div className={className + '__inner__body__attributes__multiChoice__add width-wfa flex-row-center-1rem-gap standard-link-highlight'}
                                        onClick={e => addOption(e)}
                                        disabled={cursor === 'wait'}>
                                        <Add className={className + '__inner__body__attributes__multiChoice__add__icon'}/>
                                        <div className={className + '__inner__body__attributes__multiChoice__add__text standard-text-weight'}>
                                            Add an Option ...
                                        </div>
                                    </div>
                                    <div className={className + '__inner__body__attributes__multiChoice__list width-wfa'}>
                                        {
                                            multiChoiceOptions?.map((option, index) =>
                                                <div className={className + '__inner__body__attributes__multiChoice__list__option flex-row-center-space-between width-wfa'}>
                                                    <div className={className + '__inner__body__attributes__multiChoice__list__option__left flex-row-center-1rem-gap'}>
                                                        <div className={className + '__inner__body__attributes__multiChoice__list__option__index semi-bold-text grey-text'}>
                                                            {index + 1}
                                                        </div>
                                                        <div className={className + '__inner__body__attributes__multiChoice__list__option__color flex-row-center-0p5vw-gap'}>
                                                            <div className={className + '__inner__body__attributes__multiChoice__list__option__color__icon simple-indicator-box'}
                                                                onClick={(e) => handleColorPickerMenu(e, index)}
                                                                disabled={cursor === 'wait'}
                                                                style={{background: option?.color}}/>
                                                            <ColorizeIcon className={className + '__inner__body__attributes__multiChoice__list__option__color__icon large-icon'}
                                                                onClick={(e) => handleColorPickerMenu(e, index)}/>
                                                        </div>
                                                        <div className={className + '__inner__body__attributes__multiChoice__list__option__title flex-row-center-1rem-gap'}>
                                                            <TextField
                                                                placeholder='Enter Option Title ...'
                                                                className={className + '__inner__body__attributes__multiChoice__list__option__title__textField width-wfa'}
                                                                type='text'
                                                                onChange={(e) => handleOptionTitleChange(e, index)}
                                                                inputProps={{ maxLength: 100 }}
                                                                defaultValue={option.title}
                                                                value={option?.title}
                                                                maxRows='1'
                                                                disabled={cursor === 'wait'}
                                                                variant='outlined'
                                                                required/>
                                                        </div>
                                                        <div className={className + '__inner__body__attributes__multiChoice__list__option__default'}>
                                                            <FormGroup className={className + '__inner__body__attributes__multiChoice__list__option__default__check'}>
                                                                <FormControlLabel control={<Checkbox
                                                                    checked={index === multiChoiceOptionsDefault}
                                                                    disabled={cursor === 'wait'}
                                                                    onChange={() => toggleDefaultOption(index)}/>}
                                                                    label='Set As Default'/>
                                                            </FormGroup>
                                                        </div>
                                                    </div>
                                                    <div className={className + '__inner__body__attributes__multiChoice__list__option__right flex-row-center-1rem-gap'}>
                                                        <div className={className + '__inner__body__attributes__multiChoice__list__option__delete'}>
                                                            <BoxOptions
                                                                hovered={true}
                                                                showView={false}
                                                                showShiftUp={true}
                                                                showShiftDown={true}
                                                                shiftUpDisabled={index === 0 || cursor === 'wait'}
                                                                deleteDisabled={cursor === 'wait'}
                                                                shiftDownDisabled={index === (multiChoiceOptions.length - 1) || cursor === 'wait'}
                                                                handleShiftUpClickCallback={(e) => shiftOptionUp(e, index)}
                                                                handleShiftDownClickCallback={(e) => shiftOptionDown(e, index)}
                                                                handleDeleteClickCallback={() => deleteOption(index)}/>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            }
                            {
                                fieldType === 'multiSelect' &&
                                <div className={className + '__inner__body__attributes__multiChoice width-wfa flex-column-flex-start-1vh-gap'}>
                                    <div className={className + '__inner__body__attributes__multiChoice__add width-wfa flex-row-center-1rem-gap standard-link-highlight'}
                                        onClick={e => addOption(e)}>
                                        <Add className={className + '__inner__body__attributes__multiChoice__add__icon'}/>
                                        <div className={className + '__inner__body__attributes__multiChoice__add__text standard-text-weight'}>
                                            Add an Option ...
                                        </div>
                                    </div>
                                    <div className={className + '__inner__body__attributes__multiChoice__list width-wfa'}>
                                        {
                                            multiChoiceOptions?.map((option, index) =>
                                                <div className={className + '__inner__body__attributes__multiChoice__list__option flex-row-center-space-between width-wfa'}>
                                                    <div className={className + '__inner__body__attributes__multiChoice__list__option__left flex-row-center-1rem-gap'}>
                                                        <div className={className + '__inner__body__attributes__multiChoice__list__option__index semi-bold-text grey-text'}>
                                                            {index + 1}
                                                        </div>
                                                        <div className={className + '__inner__body__attributes__multiChoice__list__option__color flex-row-center-0p5vw-gap'}>
                                                            <div className={className + '__inner__body__attributes__multiChoice__list__option__color__icon simple-indicator-box'}
                                                                onClick={(e) => handleColorPickerMenu(e, index)}
                                                                disabled={cursor === 'wait'}
                                                                style={{background: option?.color}}/>
                                                            <ColorizeIcon className={className + '__inner__body__attributes__multiChoice__list__option__color__icon large-icon'}
                                                                onClick={(e) => handleColorPickerMenu(e, index)}/>
                                                        </div>
                                                        <div className={className + '__inner__body__attributes__multiChoice__list__option__title flex-row-center-1rem-gap'}>
                                                            <TextField
                                                                placeholder='Enter Option Title ...'
                                                                className={className + '__inner__body__attributes__multiChoice__list__option__title__textField width-wfa'}
                                                                type='text'
                                                                onChange={(e) => handleOptionTitleChange(e, index)}
                                                                inputProps={{ maxLength: 100 }}
                                                                defaultValue={option.title}
                                                                value={option?.title}
                                                                disabled={cursor === 'wait'}
                                                                maxRows='1'
                                                                variant='outlined'
                                                                required/>
                                                        </div>
                                                    </div>
                                                    <div className={className + '__inner__body__attributes__multiChoice__list__option__right flex-row-center-1rem-gap'}>
                                                        <BoxOptions
                                                            hovered={true}
                                                            showView={false}
                                                            showShiftUp={true}
                                                            showShiftDown={true}
                                                            shiftUpDisabled={index === 0 || cursor === 'wait'}
                                                            deleteDisabled={cursor === 'wait'}
                                                            shiftDownDisabled={index === (multiChoiceOptions.length - 1) || cursor === 'wait'}
                                                            handleShiftUpClickCallback={(e) => shiftOptionUp(e, index)}
                                                            handleShiftDownClickCallback={(e) => shiftOptionDown(e, index)}
                                                            handleDeleteClickCallback={() => deleteOption(index)}/>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            }
                            {
                                fieldType === 'condition' &&
                                <div className={className + '__inner__body__attributes__multiChoice width-wfa flex-column-flex-start-1vh-gap'}>
                                    <div className={className + '__inner__body__attributes__multiChoice__add width-wfa flex-row-center-1rem-gap standard-link-highlight'}
                                        onClick={e => addOption(e)}
                                        disabled={cursor === 'wait'}>
                                        <Add className={className + '__inner__body__attributes__multiChoice__add__icon'}/>
                                        <div className={className + '__inner__body__attributes__multiChoice__add__text standard-text-weight'}>
                                            Add an Option ...
                                        </div>
                                    </div>
                                    <div className={className + '__inner__body__attributes__multiChoice__list width-wfa'}>
                                        {
                                            multiChoiceOptions?.map((option, index) =>
                                                <div className={className + '__inner__body__attributes__multiChoice__list__option flex-row-center-space-between width-wfa'}>
                                                    <div className={className + '__inner__body__attributes__multiChoice__list__option__left flex-row-center-1rem-gap'}>
                                                        <div className={className + '__inner__body__attributes__multiChoice__list__option__index semi-bold-text grey-text'}>
                                                            {index + 1}
                                                        </div>
                                                        <div className={className + '__inner__body__attributes__multiChoice__list__option__color flex-row-center-0p5vw-gap'}>
                                                            <div className={className + '__inner__body__attributes__multiChoice__list__option__color__icon simple-indicator-box'}
                                                                onClick={(e) => handleColorPickerMenu(e, index)}
                                                                disabled={cursor === 'wait'}
                                                                style={{background: option?.color}}/>
                                                            <ColorizeIcon className={className + '__inner__body__attributes__multiChoice__list__option__color__icon large-icon'}
                                                                onClick={(e) => handleColorPickerMenu(e, index)}/>
                                                        </div>
                                                        <div className={className + '__inner__body__attributes__multiChoice__list__option__title flex-row-center-1rem-gap'}>
                                                            <TextField
                                                                placeholder='Enter Option Title ...'
                                                                className={className + '__inner__body__attributes__multiChoice__list__option__title__textField width-wfa'}
                                                                type='text'
                                                                onChange={(e) => handleOptionTitleChange(e, index)}
                                                                inputProps={{ maxLength: 100 }}
                                                                defaultValue={option.title}
                                                                value={option?.title}
                                                                maxRows='1'
                                                                disabled={cursor === 'wait'}
                                                                variant='outlined'
                                                                required/>
                                                        </div>
                                                        <div className={className + '__inner__body__attributes__multiChoice__list__option__default'}>
                                                            <FormGroup className={className + '__inner__body__attributes__multiChoice__list__option__default__check'}>
                                                                <FormControlLabel control={<Checkbox
                                                                    checked={index === multiChoiceOptionsDefault}
                                                                    disabled={cursor === 'wait'}
                                                                    onChange={() => toggleDefaultOption(index)}/>}
                                                                    label='Set As Default'/>
                                                            </FormGroup>
                                                        </div>
                                                    </div>
                                                    <div className={className + '__inner__body__attributes__multiChoice__list__option__right flex-row-center-1rem-gap'}>
                                                        <div className={className + '__inner__body__attributes__multiChoice__list__option__delete'}>
                                                            <BoxOptions
                                                                hovered={true}
                                                                showView={false}
                                                                showShiftUp={true}
                                                                showShiftDown={true}
                                                                shiftUpDisabled={index === 0 || cursor === 'wait'}
                                                                deleteDisabled={cursor === 'wait'}
                                                                shiftDownDisabled={index === (multiChoiceOptions.length - 1) || cursor === 'wait'}
                                                                handleShiftUpClickCallback={(e) => shiftOptionUp(e, index)}
                                                                handleShiftDownClickCallback={(e) => shiftOptionDown(e, index)}
                                                                handleDeleteClickCallback={() => deleteOption(index)}/>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            }
                            {
                                fieldType === 'information' &&
                                    <div className={className + '__inner__body__attributes__information width-wfa'}>
                                        <div className={className + '__inner__body__attributes__information__text flex-column-flex-start-2vh-gap width-wfa'}>
                                             <div className={className + '__inner__body__attributes__information__text__t1 width-wfa semi-bold-text'}>
                                                 Write any text you want to be written on the form
                                             </div>
                                             <Editor
                                                 editorState={informationEditorState}
                                                 wrapperClassName='details-editor-wrapper width-wfa '
                                                 editorClassName='details-editor'
                                                 toolbarClassName='details-toolbar'
                                                 handleBeforeInput={_handleBeforeInput}
                                                 handlePastedText={_handlePastedText}
                                                 onEditorStateChange={onEditorStateChange}
                                                 disabled={cursor === 'wait'}
                                                 toolbar={{
                                                    options: ['inline', 'list'],
                                                    inline: {
                                                        options: ['bold', 'italic', 'underline'],
                                                        underline: {icon: underline},
                                                        bold: {icon: bold},
                                                        italic: {icon: italics},
                                                    },
                                                    list: {
                                                        options: ['ordered', 'unordered'],
                                                        ordered: {icon: ordList},
                                                        unordered: {icon: unordList}
                                                    },
                                                 }}
                                             />
                                             <div className={className + '__inner__body__attributes__information__text__size flex-column-flex-end-2vh-gap semi-bold-text width-wfa '
                                                 + (informationEditorSize > ( 4950) ? ' red-text' : '')}>
                                                 {informationEditorSize}/5000
                                             </div>
                                        </div>
                                    </div>
                            }
                            {
                                fieldType === 'text' &&
                                <div className={className + '__inner__body__attributes__text width-wfa'}>
                                    <div className={className + '__inner__body__attributes__text__width flex-row-center-1rem-gap width-wfa'}>
                                         <div className={className + '__inner__body__attributes__text__width__t1 semi-bold-text'}>
                                             What is the Maximum Text Size for this Field?
                                             <br/>
                                             Size: 1 - 5000
                                         </div>
                                         <TextField
                                             className={className + '__inner__body__attributes__text__width__textField'}
                                             type='number'
                                             onChange={handleTextSizeChange}
                                             value={textSize}
                                             disabled={cursor === 'wait'}
                                             InputProps={{ inputProps: { min: 1, max: 5000 } }}
                                             maxRows='1'
                                             variant='outlined'
                                             required/>
                                    </div>
                                    <div className={className + '__inner__body__attributes__text__default flex-row-center-1rem-gap width-wfa'}>
                                        <div className={className + '__inner__body__attributes__text__default__t1 semi-bold-text'}>
                                             What is the default value for this Field? (Optional)
                                        </div>
                                        <div className={className + '__inner__body__attributes__text__default__inner flex-column-flex-end'}>
                                            <TextField
                                                placeholder='Enter Default Text ...'
                                                className={className + '__inner__body__attributes__text__default__textField width-wfa'}
                                                type='text'
                                                onChange={handleDefaultTextChange}
                                                inputProps={{ maxLength: textSize }}
                                                defaultValue={defaultText}
                                                disabled={cursor === 'wait'}
                                                maxRows='3'
                                                variant='outlined'
                                                required/>
                                            <div className={className + '__title__flex__size flex-column-flex-end-2vh-gap semi-bold-text width-wfa '
                                                + (defaultTextLength > ( textSize - (textSize*0.1)) ? ' red-text' : '')}>
                                                {defaultTextLength}/{textSize}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                            {
                                fieldType === 'duration' &&
                                    <div className={className + '__inner__body__attributes__duration width-wfa'}>
                                        <div className={className + '__inner__body__attributes__duration__max flex-row-center-1rem-gap '}>
                                            <div className={className + '__inner__body__attributes__duration__max__t1 semi-bold-text'}>
                                                Is there a Maximum Duration?
                                            </div>
                                            <div className={className + '__inner__body__attributes__duration__max__value flex-column-flex-start-2vh-gap'}>
                                                <FormGroup className={className + '__inner__body__attributes__duration__max__value__check'}>
                                                    <FormControlLabel control={<Checkbox
                                                        checked={field.rules?.find((rule) => rule.type === 'maxDuration')?.set}
                                                        disabled={cursor === 'wait'}
                                                        onChange={toggleMaxDuration}/>}/>
                                                </FormGroup>
                                                {
                                                    field.rules?.find((rule) => rule.type === 'maxDuration')?.set &&
                                                        <div className={className + '__inner__body__attributes__duration__max__value__entry'}>
                                                            <FormControlLabel control={
                                                                <TextField
                                                                    className={className + '__inner__body__attributes__duration__max__value__entry__textField '}
                                                                    value={field.rules?.find((rule) => rule.type === 'maxDuration')?.days}
                                                                    type='number'
                                                                    disabled={cursor === 'wait'}
                                                                    size='small'
                                                                    InputProps={{ inputProps: { min: 0 } }}
                                                                    onChange={setMaxDurationDays}
                                                                    variant='outlined'/>
                                                                }
                                                                label='Days'/>
                                                            <FormControlLabel control={
                                                                <TextField
                                                                    onChange={setMaxDurationHours}
                                                                    className={className + '__inner__body__attributes__duration__max__value__entry__textField '}
                                                                    value={field.rules?.find((rule) => rule.type === 'maxDuration')?.hours}
                                                                    InputProps={{ inputProps: { min: 0, max: 24 } }}
                                                                    type='number'
                                                                    disabled={cursor === 'wait'}
                                                                    size='small'
                                                                    variant='outlined'/>
                                                                }
                                                                label='Hours'/>
                                                            <FormControlLabel control={
                                                                <TextField
                                                                    onChange={setMaxDurationMinutes}
                                                                    value={field.rules?.find((rule) => rule.type === 'maxDuration')?.minutes}
                                                                    className={className + '__inner__body__attributes__duration__max__value__entry__textField '}
                                                                    type='number'
                                                                    disabled={cursor === 'wait'}
                                                                    InputProps={{ inputProps: { min: 0, max: 60 } }}
                                                                    size='small'
                                                                    variant='outlined'/>
                                                                }
                                                                label='Minutes'/>
                                                            <FormControlLabel control={
                                                                <TextField
                                                                    onChange={setMaxDurationSeconds}
                                                                    value={field.rules?.find((rule) => rule.type === 'maxDuration')?.seconds}
                                                                    className={className + '__inner__body__attributes__duration__max__value__entry__textField '}
                                                                    type='number'
                                                                    disabled={cursor === 'wait'}
                                                                    InputProps={{ inputProps: { min: 0, max: 60 } }}
                                                                    size='small'
                                                                    variant='outlined'/>
                                                                }
                                                                label='Seconds'/>
                                                        </div>
                                                }
                                            </div>
                                        </div>
                                        <div className={className + '__inner__body__attributes__duration__min flex-row-center-1rem-gap width-wfa'}>
                                            <div className={className + '__inner__body__attributes__duration__min__t1 semi-bold-text'}>
                                                Is there a Minimum Duration?
                                            </div>
                                            <div className={className + '__inner__body__attributes__duration__min__value flex-column-flex-start-2vh-gap'}>
                                                <FormGroup className={className + '__inner__body__attributes__duration__min__value__check'}>
                                                    <FormControlLabel control={<Checkbox checked={field.rules?.find((rule) => rule.type === 'minDuration')?.set}
                                                        onChange={toggleMinDuration}/>}/>
                                                </FormGroup>
                                                {
                                                    field.rules?.find((rule) => rule.type === 'minDuration')?.set &&
                                                        <div className={className + '__inner__body__attributes__duration__min__value__entry'}>
                                                            <FormControlLabel control={
                                                                <TextField
                                                                    className={className + '__inner__body__attributes__duration__min__value__entry__textField '}
                                                                    value={field.rules?.find((rule) => rule.type === 'minDuration')?.days}
                                                                    type='number'
                                                                    disabled={cursor === 'wait'}
                                                                    InputProps={{ inputProps: { min: 0 } }}
                                                                    size='small'
                                                                    onChange={setMinDurationDays}
                                                                    variant='outlined'/>
                                                                }
                                                                label='Days'/>
                                                            <FormControlLabel control={
                                                                <TextField
                                                                    onChange={setMinDurationHours}
                                                                    className={className + '__inner__body__attributes__duration__min__value__entry__textField '}
                                                                    value={field.rules?.find((rule) => rule.type === 'minDuration')?.hours}
                                                                    type='number'
                                                                    disabled={cursor === 'wait'}
                                                                    InputProps={{ inputProps: { min: 0, max: 24 } }}
                                                                    size='small'
                                                                    variant='outlined'/>
                                                                }
                                                                label='Hours'/>
                                                            <FormControlLabel control={
                                                                <TextField
                                                                    onChange={setMinDurationMinutes}
                                                                    value={field.rules?.find((rule) => rule.type === 'minDuration')?.minutes}
                                                                    className={className + '__inner__body__attributes__duration__min__value__entry__textField '}
                                                                    type='number'
                                                                    disabled={cursor === 'wait'}
                                                                    InputProps={{ inputProps: { min: 0, max: 60 } }}
                                                                    size='small'
                                                                    variant='outlined'/>
                                                                }
                                                                label='Minutes'/>
                                                            <FormControlLabel control={
                                                                <TextField
                                                                    onChange={setMinDurationSeconds}
                                                                    value={field.rules?.find((rule) => rule.type === 'minDuration')?.seconds}
                                                                    className={className + '__inner__body__attributes__duration__min__value__entry__textField '}
                                                                    type='number'
                                                                    disabled={cursor === 'wait'}
                                                                    InputProps={{ inputProps: { min: 0, max: 60 } }}
                                                                    size='small'
                                                                    variant='outlined'/>
                                                                }
                                                                label='Seconds'/>
                                                        </div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                            }
                            {
                                fieldType === 'rating' &&
                                    <div className={className + '__inner__body__attributes__rating width-wfa'}>
                                        <div className={className + '__inner__body__attributes__rating__range flex-row-center-1rem-gap width-wfa'}>
                                            <div className={className + '__inner__body__attributes__rating__range__t1 semi-bold-text'}>
                                                Rating Range
                                            </div>
                                            <TextField
                                                className={className + '__inner__body__attributes__rating__range__textField '}
                                                value={ratingRange}
                                                onChange={handleRatingRangeChange}
                                                disabled={cursor === 'wait'}
                                                type='number'
                                                size='small'
                                                variant='outlined'/>
                                        </div>
                                        <div className={className + '__inner__body__attributes__rating__type flex-row-center-1rem-gap width-wfa'}>
                                            <div className={className + '__inner__body__attributes__rating__type__t1 semi-bold-text'}>
                                                Rating Type
                                            </div>
                                            <Select className={className + '__inner__body__attributes__rating__type__select standard-status-select '}
                                                variant='outlined'
                                                disabled={cursor === 'wait'}
                                                defaultValue={ratingType}
                                                onChange={(e) => handleRatingTypeChange(e)}>
                                                <MenuItem value='select' className={className + '__inner__body__attributes__rating__type__select__option'}>
                                                    <div className={className + '__inner__body__attributes__rating__type__select__option flex-row-center-1rem-gap'}>
                                                        Select
                                                    </div>
                                                </MenuItem>
                                                <MenuItem value='range' className={className + '__inner__body__attributes__rating__type__select__option'}>
                                                    <div className={className + '__inner__body__attributes__rating__type__select__option flex-row-center-1rem-gap'}>
                                                        Range
                                                    </div>
                                                </MenuItem>
                                                <MenuItem value='scale' className={className + '__inner__body__attributes__rating__type__select__option'}>
                                                    <div className={className + '__inner__body__attributes__rating__type__select__option flex-row-center-1rem-gap'}>
                                                        Scale
                                                    </div>
                                                </MenuItem>
                                            </Select>
                                        </div>
                                        {
                                            ratingType === 'scale' &&
                                                <div className={className + '__inner__body__attributes__rating__scale__icon flex-row-center-1rem-gap width-wfa'}>
                                                    <div className={className + '__inner__body__attributes__rating__scale__icon__t1 semi-bold-text'}>
                                                        Scale Icon
                                                    </div>
                                                    <Select className={className + '__inner__body__attributes__rating__scale__icon__select standard-status-select '}
                                                        variant='outlined'
                                                        disabled={cursor === 'wait'}
                                                        defaultValue={scaleIcon}
                                                        onChange={handleRatingTypeScaleIconChange}>
                                                        <MenuItem value='star' className={className + '__inner__body__attributes__rating__scale__icon__select__option'}>
                                                            <div className={className + '__inner__body__attributes__rating__scale__icon__select__option flex-row-center-1rem-gap'}>
                                                                Star: <StarIcon className={className + '__inner__body__attributes__rating__scale__icon__select__option__star'}/>
                                                            </div>
                                                        </MenuItem>
                                                        <MenuItem value='heart' className={className + '__inner__body__attributes__rating__scale__icon__select__option'}>
                                                            <div className={className + '__inner__body__attributes__rating__scale__icon__select__option flex-row-center-1rem-gap'}>
                                                                Heart: <FavoriteIcon className={className + '__inner__body__attributes__rating__scale__icon__select__option__heart'}/>
                                                            </div>
                                                        </MenuItem>
                                                        <MenuItem value='circle' className={className + '__inner__body__attributes__rating__scale__icon__select__option'}>
                                                            <div className={className + '__inner__body__attributes__rating__scale__icon__select__option flex-row-center-1rem-gap'}>
                                                                Circle: <LensIcon className={className + '__inner__body__attributes__rating__scale__icon__select__option__circle'}/>
                                                            </div>
                                                        </MenuItem>
                                                    </Select>
                                                </div>
                                        }
                                        {
                                            ratingType === 'scale' &&
                                                <div container xs={12} className={className + '__inner__body__attributes__rating__scale__display flex-column-flex-start width-wfa margin-top-2vh'}>
                                                    {
                                                        scaleIcon === 'star' &&
                                                            <Rating defaultValue={1} max={Number(ratingRange)} size='large'/>
                                                    }
                                                    {
                                                        scaleIcon === 'heart' &&
                                                            <HeartStyledRating
                                                                defaultValue={1}
                                                                getLabelText={(value) => `${value} Heart${value !== 1 ? 's' : ''}`}
                                                                max={Number(ratingRange)}
                                                                icon={<FavoriteIcon fontSize='inherit' />}
                                                                size='large'
                                                                emptyIcon={<FavoriteBorderIcon fontSize='inherit' />}/>
                                                    }
                                                    {
                                                        scaleIcon === 'circle' &&
                                                            <CircleStyledRating
                                                                defaultValue={1}
                                                                getLabelText={(value) => `${value} Heart${value !== 1 ? 's' : ''}`}
                                                                max={Number(ratingRange)}
                                                                icon={<LensIcon fontSize='inherit' />}
                                                                size='large'
                                                                emptyIcon={<RadioButtonUncheckedIcon fontSize='inherit' />}/>
                                                    }

                                                </div>
                                        }
                                        {
                                            ratingType === 'range' &&
                                                <div className={className + '__inner__body__attributes__rating__range__display flex-column-flex-start width-wfa margin-top-2vh'}>
                                                    <div className={className + '__inner__body__attributes__rating__range__display__range flex-row-center-1rem-gap'}>
                                                        <TextField
                                                            placeholder='Enter Initial Label ...'
                                                            className={className + '__inner__body__attributes__rating__range__display__range__textField '}
                                                            type='text'
                                                            disabled={cursor === 'wait'}
                                                            onChange={handleRatingTypeRangeInitialLabelChange}
                                                            inputProps={{ maxLength: 100 }}
                                                            defaultValue={ratingInitialLabel}
                                                            maxRows='1'
                                                            variant='outlined'
                                                            required/>
                                                        <FormControl className={className + '__inner__body__attributes__rating__range__display__range__list flex-column-center-2vh-gap'}>
                                                            <RadioGroup
                                                                row>
                                                                {
                                                                    ratingRangeOptions.map((option, index) =>
                                                                        <FormControlLabel labelPlacement='top' control={<Radio disabled={true} checked={false} />} label={option.value} />
                                                                    )
                                                                }
                                                            </RadioGroup>
                                                        </FormControl>
                                                        <TextField
                                                            placeholder='Enter Final Label ...'
                                                            className={className + '__inner__body__attributes__rating__range__display__range__textField '}
                                                            type='text'
                                                            disabled={cursor === 'wait'}
                                                            onChange={handleRatingTypeRangeFinalLabelChange}
                                                            inputProps={{ maxLength: 100 }}
                                                            defaultValue={ratingFinalLabel}
                                                            maxRows='1'
                                                            variant='outlined'
                                                            required/>
                                                    </div>
                                                </div>

                                        }
                                        {
                                            ratingType === 'select' &&
                                                <div className={className + '__inner__body__attributes__rating__select__display flex-column-flex-start width-wfa'}>
                                                    <div className={className + '__inner__body__attributes__rating__select__display__list flex-column-flex-start-2vh-gap'}>
                                                        {
                                                            ratingRangeOptions.map((option, index) =>
                                                                <div className={className + '__inner__body__attributes__rating__select__display__list__option flex-row-center-1rem-gap'}>
                                                                    {option.value}
                                                                    {
                                                                        option.iconType === 'emoji' &&
                                                                            <div className={className + '__inner__body__attributes__rating__select__display__list__option__emoji flex-row-center-1rem-gap'}>
                                                                                <div
                                                                                    onClick={(e) => handleEmojiClick(e, index)}
                                                                                    disabled={cursor === 'wait'}
                                                                                    className={className + '__inner__body__attributes__rating__select__display__list__option__emoji__select'}>
                                                                                    <Emoji
                                                                                        unified={option.iconValue.unified}
                                                                                        size={25}/>
                                                                                </div>
                                                                                <div className={className + '__inner__body__attributes__rating__select__display__list__option__emoji__toggle'}
                                                                                    onClick={() => handleRatingRangeIconTypeChange('text', index)}>
                                                                                    abc...
                                                                                </div>
                                                                            </div>
                                                                    }
                                                                    {
                                                                        option.iconType === 'text' &&
                                                                            <div className={className + '__inner__body__attributes__rating__select__display__list__option__text flex-row-center-1rem-gap'}>
                                                                                <TextField
                                                                                    placeholder='Enter Value ...'
                                                                                    className={className + '__inner__body__attributes__rating__select__display__list__option__text__textField '}
                                                                                    type='text'
                                                                                    disabled={cursor === 'wait'}
                                                                                    onChange={(e) => handleRatingRangeTitleChange(e, index)}
                                                                                    inputProps={{ maxLength: 100 }}
                                                                                    defaultValue={option.title}
                                                                                    maxRows='1'
                                                                                    variant='outlined'
                                                                                    required/>
                                                                                <div className={className + '__inner__body__attributes__rating__select__display__list__option__text__toggle'}
                                                                                    onClick={() => handleRatingRangeIconTypeChange('emoji', index)}>
                                                                                    <Emoji
                                                                                        unified={'1f603'}
                                                                                        size={25}/>
                                                                                </div>
                                                                            </div>
                                                                    }
                                                                    {
                                                                        option.iconType === 'emoji' && showEmojiPicker &&
                                                                            <Menu
                                                                                open={showEmojiPicker}
                                                                                onClose={handleEmojiPickerPopup}
                                                                                anchorEl={anchorEmojiPicker}
                                                                                disabled={cursor === 'wait'}
                                                                                className={className + '__inner__body__attributes__rating__select__display__list__option__emoji__picker'}>
                                                                                <EmojiPicker
                                                                                    reactionsDefaultOpen={true}
                                                                                    onEmojiClick={handleEmojiPickerSelect}/>
                                                                            </Menu>
                                                                    }
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                        }

                                    </div>
                            }
                            {
                                fieldType === 'date' &&
                                    <div className={className + '__inner__body__attributes__text width-wfa'}>
                                        <div className={className + '__inner__body__attributes__required flex-row-center-1rem-gap width-wfa'}>
                                            <div className={className + '__inner__body__attributes__required__t1 semi-bold-text'}>
                                                Allow Date Prior to Current Date?
                                            </div>
                                            <FormGroup className={className + '__inner__body__attributes__required__check'}>
                                                <FormControlLabel control={<Checkbox
                                                    checked={field.rules?.find((rule) => rule.type === 'allowPrior')?.value}
                                                    disabled={cursor === 'wait'}
                                                    onChange={toggleAllowPriorDate}/>}/>
                                            </FormGroup>
                                        </div>
                                        <div className={className + '__inner__body__attributes__required flex-row-center-1rem-gap width-wfa'}>
                                            <div className={className + '__inner__body__attributes__required__t1 semi-bold-text'}>
                                                Allow Time Selection?
                                            </div>
                                            <FormGroup className={className + '__inner__body__attributes__required__check'}>
                                                <FormControlLabel control={<Checkbox
                                                    checked={field.rules?.find((rule) => rule.type === 'timeSelect')?.value}
                                                    disabled={cursor === 'wait'}
                                                    onChange={toggleAllowTimeSelect}/>}/>
                                            </FormGroup>
                                        </div>
                                    </div>
                            }
                            {
                                fieldType === 'number' &&
                                    <div className={className + '__inner__body__attributes__number width-wfa'}>
                                        <div className={className + '__inner__body__attributes__number__default flex-row-center-1rem-gap width-wfa'}>
                                            <div className={className + '__inner__body__attributes__number__default__t1 semi-bold-text'}>
                                                Default Value (Optional)
                                            </div>
                                            <TextField
                                                className={className + '__inner__body__attributes__number__default__textField'}
                                                type='number'
                                                disabled={cursor === 'wait'}
                                                onChange={handleDefaultNumberChange}
                                                value={defaultNumber}
                                                maxRows='1'
                                                variant='outlined'
                                                required/>
                                        </div>
                                        <div className={className + '__inner__body__attributes__number__width flex-row-center-1rem-gap width-wfa'}>
                                            <div className={className + '__inner__body__attributes__number__width__t1 semi-bold-text'}>
                                                Allowed Number of digits
                                                <br/>
                                                Size: 1 - 20 (Optional)
                                            </div>
                                            <TextField
                                                className={className + '__inner__body__attributes__number__width__textField'}
                                                type='number'
                                                onChange={handleNumberDigitChange}
                                                value={numberDigitWidth}
                                                disabled={cursor === 'wait'}
                                                InputProps={{ inputProps: { min: 1, max: 20 } }}
                                                maxRows='1'
                                                variant='outlined'
                                                required/>
                                        </div>
                                        <div className={className + '__inner__body__attributes__number__width flex-row-center-1rem-gap width-wfa'}>
                                            <div className={className + '__inner__body__attributes__number__width__t1 semi-bold-text'}>
                                                Input must match the number of digits?
                                            </div>
                                            <FormGroup className={className + '__inner__body__attributes__required__check'}>
                                                <FormControlLabel control={<Checkbox
                                                    checked={field.rules?.find((rule) => rule.type === 'matchDigitWidth')?.value}
                                                    disabled={cursor === 'wait'}
                                                    onChange={toggleMatchDigitWidth}/>}/>
                                            </FormGroup>
                                        </div>
                                        <div className={className + '__inner__body__attributes__number__range flex-row-center-1rem-gap width-wfa'}>
                                            <div className={className + '__inner__body__attributes__number__range__t1 semi-bold-text'}>
                                                Input Range (Optional)
                                            </div>
                                            <div className={className + '__inner__body__attributes__number__range__t1__flex flex-column-flex-start-1vh-gap'}>
                                                <div className={className + '__inner__body__attributes__number__range__t1__flex__inner flex-row-center-1rem-gap'}>
                                                    <TextField
                                                        className={className + '__inner__body__attributes__number__range__textField'}
                                                        type='number'
                                                        label='min'
                                                        onChange={handleMinInputNumberChange}
                                                        value={numberMin ?? 0}
                                                        maxRows='1'
                                                        disabled={cursor === 'wait'}
                                                        variant='outlined'
                                                        required/>
                                                    <div className={className + '__inner__body__attributes__number__range__t2 semi-bold-text'}>
                                                        -
                                                    </div>
                                                    <TextField
                                                        className={className + '__inner__body__attributes__number__range__textField'}
                                                        type='number'
                                                        label='max'
                                                        onChange={handleMaxInputNumberChange}
                                                        value={numberMax ?? 0}
                                                        maxRows='1'
                                                        disabled={cursor === 'wait'}
                                                        variant='outlined'
                                                        required/>
                                                    <div className={className + '__inner__body__attributes__number__range__t2 standard-link semi-bold-text'}
                                                        onClick={clearRange}
                                                        disabled={cursor === 'wait'}>
                                                        Clear range
                                                    </div>
                                                </div>
                                                <div className={className + '__inner__body__attributes__number__range__t1__flex__inner__value semi-bold-text flex-row-center-1rem-gap'}>
                                                    Range: ({ (!!numberMin && !isNaN(numberMin)) ? numberMin : 'N/A'}) - ({(!!numberMax && !isNaN(numberMax)) ? numberMax : 'N/A'})
                                                </div>
                                                {
                                                    ((!!numberMin && !isNaN(numberMin) &&  (parseInt(numberMin) >= parseInt(numberMax) || parseInt(numberMin) >= parseInt(defaultNumber))) ||
                                                     (!!numberMax && !isNaN(numberMax) && (parseInt(numberMax) <= parseInt(numberMin) || parseInt(numberMax) <= parseInt(defaultNumber)))) &&
                                                    <div className={className + '__inner__body__attributes__number__range__error red-text width-wfa flex-column-flex-start-1vh-gap'}>
                                                        <div>
                                                            Invalid Range
                                                        </div>
                                                        {
                                                            !!numberMin && !isNaN(numberMin) && (parseInt(numberMin) >= parseInt(numberMax)) &&
                                                                <div>
                                                                    Minimum Value needs to be LESSER than the Maximum Value
                                                                </div>
                                                        }
                                                        {
                                                            !!numberMin && !isNaN(numberMin) && (parseInt(numberMin) >= parseInt(defaultNumber)) &&
                                                                <div>
                                                                    Minimum Value needs to be LESSER than the Default Value
                                                                </div>
                                                        }
                                                        {
                                                            !!numberMax && !isNaN(numberMax) && (parseInt(numberMax) <= parseInt(defaultNumber)) &&
                                                                <div>
                                                                    Maximum Value needs to be GREATER than the Default Value
                                                                </div>
                                                        }
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                            }
                            {
                                fieldType === 'file' &&
                                    <div className={className + '__inner__body__attributes__file width-wfa'}>
                                        <div className={className + '__inner__body__attributes__file__note red-text width-wfa'}>
                                            Note: The total File Attachments size for any Ticket is 5MB.
                                        </div>
                                        <div className={className + '__inner__body__attributes__file__options flex-row-center-1rem-gap'}>
                                            <div className={className + '__inner__body__attributes__file__options__t1 semi-bold-text grey-text'}>
                                                Allowed File Types
                                            </div>
                                            <FormGroup className={className + '__inner__body__attributes__file__options__list'}>
                                                <FormControlLabel control={<Checkbox checked={
                                                        field?.rules[field?.rules?.findIndex((rule) => rule.type === 'acceptTypes')].value?.findIndex((type) => type === '.png') !== -1
                                                    }
                                                    disabled={cursor === 'wait'}
                                                    onChange={() => toggleFileType('.png')}/>}
                                                    label='.png'/>
                                                <FormControlLabel control={<Checkbox checked={
                                                        field?.rules[field?.rules?.findIndex((rule) => rule.type === 'acceptTypes')].value?.findIndex((type) => type === '.jpg') !== -1
                                                    }
                                                    disabled={cursor === 'wait'}
                                                    onChange={() => toggleFileType('.jpg')}/>}
                                                    label='.jpg'/>
                                                <FormControlLabel control={<Checkbox checked={
                                                        field?.rules[field?.rules?.findIndex((rule) => rule.type === 'acceptTypes')].value?.findIndex((type) => type === '.jpeg') !== -1
                                                    }
                                                    disabled={cursor === 'wait'}
                                                    onChange={() => toggleFileType('.jpeg')}/>}
                                                    label='.jpeg'/>
                                                <FormControlLabel control={<Checkbox checked={
                                                        field?.rules[field?.rules?.findIndex((rule) => rule.type === 'acceptTypes')].value?.findIndex((type) => type === '.svg') !== -1
                                                    }
                                                    disabled={cursor === 'wait'}
                                                    onChange={() => toggleFileType('.svg')}/>}
                                                    label='.svg'/>
                                                <FormControlLabel control={<Checkbox checked={
                                                        field?.rules[field?.rules?.findIndex((rule) => rule.type === 'acceptTypes')].value?.findIndex((type) => type === '.webp') !== -1
                                                    }
                                                    disabled={cursor === 'wait'}
                                                    onChange={() => toggleFileType('.webp')}/>}
                                                    label='.webp'/>
                                                <FormControlLabel control={<Checkbox checked={
                                                        field?.rules[field?.rules?.findIndex((rule) => rule.type === 'acceptTypes')].value?.findIndex((type) => type === '.gif') !== -1
                                                    }
                                                    disabled={cursor === 'wait'}
                                                    onChange={() => toggleFileType('.gif')}/>}
                                                    label='.gif'/>
                                                <FormControlLabel control={<Checkbox checked={
                                                        field?.rules[field?.rules?.findIndex((rule) => rule.type === 'acceptTypes')].value?.findIndex((type) => type === '.pdf') !== -1
                                                    }
                                                    disabled={cursor === 'wait'}
                                                    onChange={() => toggleFileType('.pdf')}/>}
                                                    label='.pdf'/>
                                                <FormControlLabel control={<Checkbox checked={
                                                        field?.rules[field?.rules?.findIndex((rule) => rule.type === 'acceptTypes')].value?.findIndex((type) => type === '.doc') !== -1
                                                    }
                                                    disabled={cursor === 'wait'}
                                                    onChange={() => toggleFileType('.doc')}/>}
                                                    label='.doc'/>
                                                <FormControlLabel control={<Checkbox checked={
                                                        field?.rules[field?.rules?.findIndex((rule) => rule.type === 'acceptTypes')].value?.findIndex((type) => type === '.docx') !== -1
                                                    }
                                                    disabled={cursor === 'wait'}
                                                    onChange={() => toggleFileType('.docx')}/>}
                                                    label='.docx'/>
                                                <FormControlLabel control={<Checkbox checked={
                                                        field?.rules[field?.rules?.findIndex((rule) => rule.type === 'acceptTypes')].value?.findIndex((type) => type === '.txt') !== -1
                                                    }
                                                    disabled={cursor === 'wait'}
                                                    onChange={() => toggleFileType('.txt')}/>}
                                                    label='.txt'/>
                                                <FormControlLabel control={<Checkbox checked={
                                                        field?.rules[field?.rules?.findIndex((rule) => rule.type === 'acceptTypes')].value?.findIndex((type) => type === '.xls') !== -1
                                                    }
                                                    disabled={cursor === 'wait'}
                                                    onChange={() => toggleFileType('.xls')}/>}
                                                    label='.xls'/>
                                                <FormControlLabel control={<Checkbox checked={
                                                        field?.rules[field?.rules?.findIndex((rule) => rule.type === 'acceptTypes')].value?.findIndex((type) => type === '.mp4') !== -1
                                                    }
                                                    disabled={cursor === 'wait'}
                                                    onChange={() => toggleFileType('.mp4')}/>}
                                                    label='.mp4'/>
                                                <FormControlLabel control={<Checkbox checked={
                                                        field?.rules[field?.rules?.findIndex((rule) => rule.type === 'acceptTypes')].value?.findIndex((type) => type === '.3gp') !== -1
                                                    }
                                                    disabled={cursor === 'wait'}
                                                    onChange={() => toggleFileType('.3gp')}/>}
                                                    label='.3gp'/>
                                            </FormGroup>
                                        </div>
                                    </div>
                            }
                        </div>
                    </div>
                    <div className={className + '__buttonFlex flex-row-center-1rem-gap width-wfa'}>
                        <div className={className + '__buttonFlex__create'}>
                            <BorderBlueButtonV2
                                text='Create'
                                disabled={isSubmitDisabled || cursor === 'wait'}
                                onClick={handleSubmit}/>
                        </div>
                        <div className={className + '__buttonFlex__cancel'}>
                            <BorderWhiteButtonV2
                                text='Cancel'
                                disabled={cursor === 'wait'}
                                onClick={props.closePopup}/>
                        </div>
                    </div>
                    {
                        showColorMenu &&
                            <ColorPicker
                                showColorMenu={showColorMenu}
                                anchorColorMenu={anchorColorMenu}
                                handleColorChange={handleOptionColorChange}
                                handleColorPickerMenu={handleColorPickerMenu}
                                color={colorPickerColor}/>
                    }
                </div>
        </Modal>
    );
}
